import type React from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Columns3,
  ChevronDown,
  GripVertical,
  Loader2,
  RotateCcw,
  Search,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import type { Column } from "../../types";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  resetContainerOrderColumns,
  updateContainerOrderColumns,
} from "../../store/slices/containerOrdersSlice";
import { useConfirm } from "../../hooks/useConfirm";
import {
  containerOrderColumnLabelKey,
  resolveContainerOrderColumns,
} from "../../utils/containerOrderColumns";

/**
 * Column settings for the container orders list.
 *
 * The same idea as the shipments panel — drag to reorder, switch to hide, one
 * button back to the default layout — with the parts that list learned the hard
 * way tightened up:
 *
 *  - the panel never adopts a server response over an edit the user has made
 *    since (see `syncFromStore` below), so a save landing mid-drag cannot undo
 *    the drag;
 *  - a failed save rolls the panel back to what the server actually holds,
 *    rather than leaving the panel showing an arrangement the table does not;
 *  - the last visible column cannot be switched off, so the table can never be
 *    reduced to a column of checkboxes;
 *  - closing the panel — by button, by click-outside, or by navigating away —
 *    flushes a pending save instead of dropping it.
 *
 * It reads and writes the store itself: the list's only job is to draw whatever
 * `state.containerOrders.columns` currently says.
 */

/** How long a change sits before it is written, so a drag is one request. */
const SAVE_DEBOUNCE_MS = 800;

/** Are two arrangements the same? Decides whether there is anything to save. */
const sameArrangement = (a: Column[], b: Column[]) =>
  a.length === b.length &&
  a.every(
    (col, i) => b[i] && col.id === b[i].id && col.visible === b[i].visible,
  );

interface ContainerOrderColumnConfigProps {
  /**
   * Has the initial settings request settled?
   *
   * The panel stays shut until it has: opening it over the fallback layout
   * would let a toggle be made against defaults, and the debounced save that
   * followed would write those defaults over the arrangement still in flight.
   */
  ready: boolean;
}

export const ContainerOrderColumnConfig: React.FC<
  ContainerOrderColumnConfigProps
> = ({ ready }) => {
  const { t } = useTranslation("containerOrders");
  const dispatch = useAppDispatch();
  const { confirm, confirmDialog } = useConfirm();

  const storedColumns = useAppSelector(
    (state) => state.containerOrders.columns,
  );
  // Anything the table cannot draw is dropped here too, so the panel never
  // offers a switch for a column that has no cell behind it.
  const serverColumns = resolveContainerOrderColumns(storedColumns);

  const [isOpen, setIsOpen] = useState(false);
  /** Free-text narrowing of the row list — see `shownColumns` below. */
  const [query, setQuery] = useState("");
  const [localColumns, setLocalColumns] = useState<Column[]>(serverColumns);
  /**
   * Has the *user* changed something that is not yet written?
   *
   * Explicitly tracked, rather than inferred by diffing the panel against the
   * store, because those two are not the same question and answering the wrong
   * one silently destroyed people's settings: on mount the panel holds the
   * default layout and the store holds nothing, so the moment the saved
   * arrangement arrived the panel read as "changed" and wrote the defaults
   * straight back over it. Nothing here saves unless a hand touched a control.
   */
  const [hasEdit, setHasEdit] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  // The reset confirmation portals to the document body, so from this panel's
  // point of view every click and every Escape inside it lands "outside". While
  // one is open the dismiss handlers stand down, or answering the question
  // would close the panel out from behind it.
  const [isConfirming, setIsConfirming] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // The panel's state, mirrored into refs so the flush-on-close and
  // flush-on-unmount paths can read it without re-subscribing their listeners
  // on every keystroke of a drag.
  const localRef = useRef(localColumns);
  const serverRef = useRef(serverColumns);
  const editRef = useRef(hasEdit);
  const queryRef = useRef(query);
  const savingRef = useRef(false);
  localRef.current = localColumns;
  serverRef.current = serverColumns;
  editRef.current = hasEdit;
  queryRef.current = query;

  /** Every control that changes the arrangement goes through this. */
  const applyEdit = (next: (prev: Column[]) => Column[]) => {
    setLocalColumns(next);
    setHasEdit(true);
  };

  /**
   * Adopt the server's arrangement whenever the panel has nothing unsaved.
   *
   * This is what loads the user's saved columns into the panel, and it is also
   * what settles it after a write — a save answers with the *reconciled* list,
   * which is not always what was sent. It stands down only for an edit made
   * since, so a response landing mid-drag cannot undo the drag.
   */
  useEffect(() => {
    if (hasEdit || savingRef.current) return;
    setLocalColumns(serverRef.current);
    // `serverColumns` is rebuilt each render; the ids, their order and their
    // visibility are what matter, so the effect keys on a description of those
    // rather than on the array's identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverColumns.map((c) => `${c.id}:${c.visible}`).join("|"), hasEdit]);

  /**
   * Persist the current arrangement.
   *
   * Guarded against re-entry: the debounce timer, the close handler and the
   * unmount cleanup can all reach this, and a container order's settings are a
   * single document — two writes in flight would race to be last.
   */
  const save = useCallback(async () => {
    // No edit, nothing to write. This is the guard that makes a page load
    // read-only: without a hand on a control there is no request at all.
    if (savingRef.current || !editRef.current) return;

    const next = localRef.current;
    // An edit that landed back where it started is not worth a round trip.
    if (sameArrangement(next, serverRef.current)) {
      setHasEdit(false);
      return;
    }

    savingRef.current = true;
    setIsSaving(true);
    try {
      const saved = await dispatch(updateContainerOrderColumns(next)).unwrap();
      // Converge on what the server stored, which is not always what was sent:
      // it appends columns this client did not know about and drops ones it no
      // longer has. Without adopting the answer, the panel would still read as
      // dirty against it and the debounce would save the same payload forever.
      // Skipped if the user has moved on since — their newer edit is the one
      // that should win, and it will be saved by the next pass.
      if (localRef.current === next) {
        setLocalColumns(resolveContainerOrderColumns(saved));
        setHasEdit(false);
      }
    } catch (error) {
      // Put the panel back on what the server actually holds. Leaving the
      // user's arrangement here would show switches that disagree with the
      // table beside them, with nothing to say which one is real. An edit made
      // while the failing request was in flight is left alone — it has not been
      // tried yet, and the debounce will try it.
      if (localRef.current === next) {
        setLocalColumns(serverRef.current);
        // Clearing the edit is what stops a permanently failing save from
        // retrying every second for as long as the page is open.
        setHasEdit(false);
      }
      toast.error(typeof error === "string" ? error : t("columns.saveFailed"));
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [dispatch, t]);

  // Everything below reaches `save` through this ref rather than through the
  // callback itself. `save` is rebuilt whenever the translation function is,
  // and hanging the debounce timer and the unmount flush off that identity
  // meant a re-render could cancel a pending write, or run one early.
  const saveRef = useRef(save);
  saveRef.current = save;

  // Debounced write. A drag is a burst of reorders and a spree of toggles is a
  // burst of flips; both settle into one request.
  useEffect(() => {
    if (!hasEdit) return;
    const timer = setTimeout(() => void saveRef.current(), SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [localColumns, hasEdit]);

  // Flush whatever is pending when the panel goes away for any reason — the
  // debounce timer is cleared on unmount, so without this the last change
  // before a navigation is simply lost.
  useEffect(() => () => void saveRef.current(), []);

  const close = useCallback(() => {
    setIsOpen(false);
    // A stale search would greet the user with three of twenty-six rows next
    // time they open the panel, with no obvious reason why.
    setQuery("");
    void saveRef.current();
  }, []);

  useEffect(() => {
    if (!isOpen || isConfirming) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isConfirming, close]);

  // ── Editing ───────────────────────────────────────────────────────────────

  const visibleCount = localColumns.filter((c) => c.visible).length;

  const toggleVisibility = (column: Column) => {
    if (column.fixed) return;
    // The guard is here as well as on the server: an empty table is a dead end
    // the user would have to find the reset button to escape from.
    if (column.visible && visibleCount <= 1) {
      toast.info(t("columns.lastVisible"));
      return;
    }
    applyEdit((prev) =>
      prev.map((c) => (c.id === column.id ? { ...c, visible: !c.visible } : c)),
    );
  };

  /** Move a column to a new index, clamped — used by drag and by keyboard. */
  const moveColumn = (columnId: string, toIndex: number) => {
    applyEdit((prev) => {
      const from = prev.findIndex((c) => c.id === columnId);
      if (from === -1) return prev;
      const target = Math.max(0, Math.min(prev.length - 1, toIndex));
      if (target === from) return prev;

      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(target, 0, moved);
      return next;
    });
  };

  const handleDrop = (targetId: string) => {
    if (draggedId && draggedId !== targetId) {
      // The target's index is read *before* the move: dropping onto a row
      // below means "put it after that row", onto one above means "before".
      // Removing the dragged row first shifts the indices exactly enough for
      // one insert position to express both.
      const targetIndex = localColumns.findIndex((c) => c.id === targetId);
      if (targetIndex !== -1) moveColumn(draggedId, targetIndex);
    }
    setDraggedId(null);
    setDragOverId(null);
  };

  /** Alt+Arrow reorders without a mouse — drag alone is unreachable. */
  const handleRowKeyDown = (e: React.KeyboardEvent, index: number) => {
    // Same reason the drag handle is disabled while filtering: "one row up"
    // has no answer when the row above is hidden by the search.
    if (queryRef.current) return;
    if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    e.preventDefault();
    moveColumn(localColumns[index].id, index + (e.key === "ArrowUp" ? -1 : 1));
  };

  const showAll = () =>
    applyEdit((prev) => prev.map((c) => ({ ...c, visible: true })));

  const handleReset = async () => {
    setIsConfirming(true);
    const agreed = await confirm({
      title: t("confirm.titles.resetColumns"),
      description: t("confirm.resetColumns"),
      confirmLabel: t("columns.reset"),
      tone: "neutral",
    });
    setIsConfirming(false);
    if (!agreed) return;

    setIsResetting(true);
    // Blocks the debounce from writing the arrangement being discarded on top
    // of the reset that is replacing it.
    savingRef.current = true;
    try {
      const columns = await dispatch(resetContainerOrderColumns()).unwrap();
      setLocalColumns(resolveContainerOrderColumns(columns));
      // The reset *is* the write, so anything pending has been superseded.
      setHasEdit(false);
      toast.success(t("columns.resetSucceeded"));
    } catch (error) {
      toast.error(typeof error === "string" ? error : t("columns.resetFailed"));
    } finally {
      savingRef.current = false;
      setIsResetting(false);
    }
  };

  // ── Fitting the panel on screen ───────────────────────────────────────────

  /**
   * How tall the panel may be, and which way it opens.
   *
   * Measured rather than expressed in CSS. A `max-height` in viewport units
   * describes the window, not the space the panel actually has: the button sits
   * some way down the page, so `100vh - something` overflows by however far
   * down it sits — which is exactly what a list this long made obvious. The
   * numbers below come from the button's own rectangle, so the panel fits
   * wherever the toolbar has wrapped it to.
   */
  const [fit, setFit] = useState<{ maxHeight: number; dropUp: boolean }>({
    maxHeight: 0,
    dropUp: false,
  });

  useLayoutEffect(() => {
    if (!isOpen) return;

    const measure = () => {
      const el = containerRef.current;
      if (!el) return;

      // Below `sm` the panel is a viewport-pinned bottom sheet and sizes
      // itself; measuring against the button there would fight its own CSS.
      if (!window.matchMedia("(min-width: 640px)").matches) {
        setFit({ maxHeight: 0, dropUp: false });
        return;
      }

      const rect = el.getBoundingClientRect();
      const gutter = 16;
      const below = Math.round(window.innerHeight - rect.bottom - gutter);
      const above = Math.round(rect.top - gutter);
      // Opening downwards is the expectation, so it takes more than "less
      // room" to flip: only when what is below is too cramped to be worth
      // using *and* above is genuinely roomier.
      const dropUp = below < 280 && above > below;
      // A floor keeps the panel usable rather than collapsing to a sliver in a
      // short window; it scrolls within whatever it gets.
      const maxHeight = Math.max(220, dropUp ? above : below);

      // Returning `prev` unchanged is what makes the capture-phase listener
      // below affordable: it hears every scroll on the page, the panel's own
      // list included, and re-rendering the panel on each frame of its own
      // scrolling would make it stutter. Only the button actually moving
      // changes these numbers.
      setFit((prev) =>
        prev.maxHeight === maxHeight && prev.dropUp === dropUp
          ? prev
          : { maxHeight, dropUp },
      );
    };

    measure();
    // `true` catches scrolls in any ancestor, not just the window: the list
    // page scrolls its own containers, and the button moves with them.
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [isOpen]);

  // ── Render ────────────────────────────────────────────────────────────────

  /** The header this column shows in the table, in the current language. */
  const labelFor = (column: Column) =>
    t(containerOrderColumnLabelKey(column.id), { defaultValue: column.name });

  /**
   * The rows on show. With twenty-six columns the list is longer than the panel
   * can hold, so it can be narrowed by name — reaching "Net profit" should not
   * mean scrolling past twenty other switches.
   */
  const needle = query.trim().toLowerCase();
  const shownColumns = needle
    ? localColumns.filter((c) => labelFor(c).toLowerCase().includes(needle))
    : localColumns;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        aria-expanded={isOpen}
        disabled={!ready}
        title={ready ? undefined : t("columns.loading")}
        className="inline-flex items-center gap-2 rounded-md border border-[rgba(11,15,14,0.15)] bg-white px-3 py-1.5 text-[12px] font-medium text-secondary-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-[rgba(11,15,14,0.15)] disabled:hover:bg-white disabled:hover:text-secondary-700"
      >
        <Columns3 className="h-3.5 w-3.5" />
        <span>{t("columns.button")}</span>
        {(isSaving || !ready) && (
          <Loader2 className="h-3 w-3 animate-spin text-mist" />
        )}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Below `sm` the panel is a bottom sheet, matching the filter panel it
          sits beside; the scrim both explains that and gives a large target to
          dismiss it with. */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 sm:hidden"
          onClick={close}
          aria-hidden
        />
      )}

      {isOpen && (
        <div
          // The measured height wins over the class fallback, which only
          // covers the first paint before `useLayoutEffect` has run.
          style={fit.maxHeight ? { maxHeight: fit.maxHeight } : undefined}
          className={`absolute left-0 right-auto z-50 flex max-h-[calc(100vh-7rem)] w-[calc(100vw-2rem)] max-w-80 flex-col overflow-hidden rounded-lg border border-[rgba(11,15,14,0.15)] bg-white shadow-xl max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:mt-0 max-sm:max-h-[85vh] max-sm:w-auto max-sm:max-w-none max-sm:rounded-b-none max-sm:border-x-0 sm:left-auto sm:right-0 sm:w-80 ${
            fit.dropUp ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          <div className="flex shrink-0 items-center justify-between border-b border-[rgba(11,15,14,0.08)] bg-bone px-3 py-2">
            <div className="min-w-0">
              <div className="text-[12px] font-bold text-ink">
                {t("columns.title")}
              </div>
              <div className="text-[10px] text-mist">
                {t("columns.visibleCount", {
                  shown: visibleCount,
                  total: localColumns.length,
                })}
              </div>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label={t("columns.close")}
              className="text-mist transition-colors hover:text-ink"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Pinned above the scroll area, with the hint, so both stay put
              however far down the list the user has scrolled. */}
          <div className="shrink-0 border-b border-[rgba(11,15,14,0.08)] px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-mist" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("columns.searchPlaceholder")}
                aria-label={t("columns.searchPlaceholder")}
                className="w-full rounded-md border border-[rgba(11,15,14,0.15)] py-1 pl-7 pr-7 text-[11px] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label={t("columns.clearSearch")}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-mist transition-colors hover:text-ink"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            <p className="mt-1.5 text-[10px] leading-snug text-mist">
              {/* Reordering is meaningless against a filtered list — dropping a
                  row "after" one that is hidden by the search has no defined
                  answer — so the hint says which mode the list is in. */}
              {needle ? t("columns.hintFiltered") : t("columns.hint")}
            </p>
          </div>

          <div className="dropdown-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2">
            {shownColumns.length === 0 && (
              <p className="px-2 py-6 text-center text-[11px] text-mist">
                {t("columns.noMatches", { query: query.trim() })}
              </p>
            )}
            <ul className="space-y-0.5">
              {shownColumns.map((column) => {
                // The row's place in the real list, not in the filtered view:
                // keyboard reordering has to move it among all its siblings.
                const index = localColumns.findIndex((c) => c.id === column.id);
                const isLastVisible = column.visible && visibleCount <= 1;
                const locked = column.fixed || isLastVisible;

                return (
                  <li
                    key={column.id}
                    draggable={!needle}
                    tabIndex={0}
                    onDragStart={() => setDraggedId(column.id)}
                    onDragEnd={() => {
                      setDraggedId(null);
                      setDragOverId(null);
                    }}
                    onDragOver={(e) => {
                      if (needle) return;
                      e.preventDefault();
                      setDragOverId(column.id);
                    }}
                    onDragLeave={() => setDragOverId(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleDrop(column.id);
                    }}
                    onKeyDown={(e) => handleRowKeyDown(e, index)}
                    className={`flex items-center justify-between gap-2 rounded-md px-2 py-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                      needle ? "cursor-default" : "cursor-move"
                    } ${
                      dragOverId === column.id && draggedId !== column.id
                        ? "bg-primary-50 ring-1 ring-primary-300"
                        : "hover:bg-bone"
                    } ${draggedId === column.id ? "opacity-40" : ""}`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <GripVertical
                        className={`h-3.5 w-3.5 shrink-0 ${
                          needle ? "text-gray-200" : "text-mist"
                        }`}
                      />
                      <span className="truncate text-[11px] text-gray-700">
                        {labelFor(column)}
                      </span>
                      {column.fixed && (
                        <span className="shrink-0 rounded bg-secondary-100 px-1 py-0.5 text-[9px] font-semibold uppercase text-secondary-600">
                          {t("columns.fixed")}
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      role="switch"
                      aria-checked={column.visible}
                      aria-label={
                        column.visible
                          ? t("columns.hideColumn", {
                              name: labelFor(column),
                            })
                          : t("columns.showColumn", {
                              name: labelFor(column),
                            })
                      }
                      title={
                        column.fixed
                          ? t("columns.fixedHint")
                          : isLastVisible
                            ? t("columns.lastVisible")
                            : undefined
                      }
                      disabled={locked}
                      draggable={false}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => toggleVisibility(column)}
                      className={`relative h-4 w-8 shrink-0 rounded-full transition-colors duration-200 ${
                        column.visible ? "bg-primary" : "bg-gray-300"
                      } ${locked ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                    >
                      {/* `left-0.5` anchors the knob explicitly. Without a
                          `left` an absolutely positioned box falls back to its
                          static position, and a <button> centres its content —
                          so "off" sat mid-track and read as half-on, while "on"
                          was pushed past the right edge and vanished. Anchored
                          at the left, the whole travel is the transform. */}
                      <span
                        className={`absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                          column.visible ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="flex shrink-0 items-center justify-between gap-2 border-t border-[rgba(11,15,14,0.08)] bg-bone px-3 py-2">
            <button
              type="button"
              onClick={showAll}
              disabled={visibleCount === localColumns.length}
              className="text-[11px] font-medium text-secondary-700 transition-colors hover:text-primary-700 disabled:cursor-not-allowed disabled:text-mist disabled:hover:text-mist"
            >
              {t("columns.showAll")}
            </button>

            <button
              type="button"
              onClick={handleReset}
              disabled={isResetting}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[11px] font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-60"
            >
              {isResetting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5" />
              )}
              {isResetting ? t("columns.resetting") : t("columns.reset")}
            </button>
          </div>
        </div>
      )}

      {confirmDialog}
    </div>
  );
};

export default ContainerOrderColumnConfig;
